import type { ReactNode } from 'react';
import type { Theme } from '@mui/material/styles';
import type { TableContainerProps } from '@mui/material/TableContainer';

import { useRef, useState, useEffect, useLayoutEffect } from 'react';

import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import TableContainer from '@mui/material/TableContainer';

import { useLocales } from 'src/locales';

// ----------------------------------------------------------------------

export type DataTableContainerProps = Omit<TableContainerProps, 'children'> & {
  children: ReactNode;
  /** The internal vertical scroll that keeps stickyHeader working; `null` for none. Default '70vh'. */
  maxHeight?: string | number | null;
  /** Names the scroll region for keyboard and screen-reader users. */
  'aria-label'?: string;
  /** The fixed bar at the viewport bottom. Off inside a Dialog, where a fixed bar floats outside it. Default true. */
  proxyScrollbar?: boolean;
};

/**
 * At least 3:1 against background.paper in both schemes (WCAG 1.4.11). The app themes through CSS
 * variables, so `palette.mode` is always 'light': the dark thumb goes through `applyStyles('dark')`.
 */
const thumbColor = (theme: Theme, scheme: 'light' | 'dark') =>
  alpha(scheme === 'dark' ? theme.palette.grey[400] : theme.palette.grey[600], 0.8);

const track = (theme: Theme) => alpha(theme.palette.grey[500], 0.08);

/**
 * NAT-2711 — the one container every data table sits in.
 *
 * The table's own horizontal scrollbar sits at its foot, often hundreds of pixels below the
 * fold. While the table is wider than its box, its top is on screen and that scrollbar is below
 * the viewport, a thin proxy bar is pinned to the viewport bottom over the table's width and
 * scrolls the table with it. It is `position: fixed`, not `sticky`: most tables sit in a Card
 * with `overflow: hidden`, which would become the sticky container and keep the bar below the
 * fold. (No ancestor in the dashboard layout has a transform, so no portal is needed.)
 *
 * Two tables on one page: the proxy only shows for the table that spans the viewport's bottom
 * edge (top above it, scrollbar below it), and two stacked tables cannot both do that.
 */
export function DataTableContainer({
  children,
  maxHeight = '70vh',
  'aria-label': ariaLabel,
  proxyScrollbar = true,
  sx,
  onScroll,
  ...other
}: DataTableContainerProps) {
  const { t } = useLocales();

  const scrollerRef = useRef<HTMLDivElement>(null);
  const proxyRef = useRef<HTMLDivElement>(null);

  const [state, setState] = useState(HIDDEN);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;

    const update = () => {
      const rect = scroller.getBoundingClientRect();
      const overflowing = scroller.scrollWidth > scroller.clientWidth + 1;
      const next: ProxyState = {
        overflowing,
        visible: overflowing && rect.bottom > window.innerHeight && rect.top < window.innerHeight,
        left: rect.left,
        width: rect.width,
        scrollWidth: scroller.scrollWidth,
        clientWidth: scroller.clientWidth,
      };
      setState((prev) =>
        (Object.keys(next) as Array<keyof ProxyState>).every((k) => prev[k] === next[k])
          ? prev
          : next,
      );
    };

    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        update();
      });
    };

    update();
    // Capture: a scroll in any ancestor scroller moves the table on screen too.
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(schedule);
      observer.observe(scroller);
      if (scroller.firstElementChild) observer.observe(scroller.firstElementChild);
      // A sidebar transition shifts the table sideways without resizing it; the layout's main
      // box (or, failing that, the scroller's offset parent) does resize.
      const shifter = document.querySelector('main') ?? scroller.offsetParent;
      if (shifter) observer.observe(shifter);
    }
    return () => {
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      observer?.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  // A proxy hidden with display:none loses its offset: take the table's when it appears.
  useLayoutEffect(() => {
    if (state.visible) sync(scrollerRef.current, proxyRef.current);
  }, [state.visible, state.scrollWidth, state.width]);

  return (
    <Box sx={{ position: 'relative' }}>
      <TableContainer
        {...other}
        ref={scrollerRef}
        // A scroll region is only a tab stop when there is something to scroll.
        role={state.overflowing ? 'region' : undefined}
        tabIndex={state.overflowing ? 0 : undefined}
        aria-label={state.overflowing ? (ariaLabel ?? t('table_scroll_region')) : ariaLabel}
        onScroll={(event) => {
          sync(scrollerRef.current, proxyRef.current);
          onScroll?.(event);
        }}
        sx={[
          { overflow: 'auto', ...(maxHeight !== null && { maxHeight }) },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {children}
      </TableContainer>

      {proxyScrollbar && (
        <Box
          ref={proxyRef}
          // Not `inert`: that removes it from hit-testing and the scrollbar could not be dragged.
          aria-hidden
          tabIndex={-1}
          data-testid="table-scroll-proxy"
          onScroll={() => sync(proxyRef.current, scrollerRef.current)}
          style={{ left: state.left, width: state.width }}
          sx={{
            display: state.visible ? 'block' : 'none',
            position: 'fixed',
            bottom: 0,
            zIndex: 3,
            overflowX: 'auto',
            overflowY: 'hidden',
            height: 12,
            // Custom scrollbar styles make the browser draw a classic bar, so the handle is
            // visible at idle even where the OS default is an auto-hiding overlay bar. Chromium
            // ignores the ::-webkit-scrollbar rules once scrollbar-width or scrollbar-color is
            // set, so those two are for engines without the pseudo-elements (Firefox). Firefox's
            // overlay scrollbars still hide the handle at idle: NAT-2715.
            '@supports not selector(::-webkit-scrollbar)': (theme: Theme) => ({
              scrollbarWidth: 'thin',
              scrollbarColor: `${thumbColor(theme, 'light')} ${track(theme)}`,
              ...theme.applyStyles('dark', {
                scrollbarColor: `${thumbColor(theme, 'dark')} ${track(theme)}`,
              }),
            }),
            '&::-webkit-scrollbar': { height: 10 },
            '&::-webkit-scrollbar-thumb': (theme: Theme) => ({
              background: thumbColor(theme, 'light'),
              borderRadius: '5px',
              ...theme.applyStyles('dark', { background: thumbColor(theme, 'dark') }),
            }),
            '&::-webkit-scrollbar-track': {
              background: (theme: Theme) => track(theme),
            },
            backgroundColor: 'background.paper',
            '@media print': { display: 'none' },
            // Touch scrolls the table directly.
            '@media (pointer: coarse)': { display: 'none' },
          }}
        >
          {/* Both ranges end together: scrollWidth - clientWidth of the table, on the proxy's own width. */}
          <Box
            data-testid="table-scroll-proxy-inner"
            // '1px', not 1: a bare number in (0, 1] is a percentage in sx, and 100% of 12px collapses.
            sx={{ height: '1px' }}
            style={{ width: state.scrollWidth - state.clientWidth + state.width }}
          />
        </Box>
      )}
    </Box>
  );
}

type ProxyState = {
  overflowing: boolean;
  visible: boolean;
  left: number;
  width: number;
  scrollWidth: number;
  clientWidth: number;
};

const HIDDEN: ProxyState = {
  overflowing: false,
  visible: false,
  left: 0,
  width: 0,
  scrollWidth: 0,
  clientWidth: 0,
};

/** Copy scrollLeft across; equal within a pixel stops the echo a fractional value would start. */
function sync(from: HTMLElement | null, to: HTMLElement | null) {
  if (from && to && Math.abs(to.scrollLeft - from.scrollLeft) >= 1) to.scrollLeft = from.scrollLeft;
}
